import React, { useState } from "react";
import { styled } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import MuiExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MuiList from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import MuiListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import pxToRem from "libs/pxToRem";
import { IListItem } from "api/types/originacion/commonTypes";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { format, addMonths } from "date-fns";
import { es } from "date-fns/locale";

interface IPrimerVencimientoSelector {
  originalDate: string;
  selectedDate: string;
  onSelect: (newDate: string) => void;
}

const PrimerVencimientoSelector: React.FC<IPrimerVencimientoSelector> = ({
  originalDate,
  selectedDate,
  onSelect,
}) => {
  const [optionsExpanded, setOptionsExpanded] = useState(true);

  const original = new Date(originalDate);
  const plus1Month = addMonths(original, 1);
  const plus2Months = addMonths(original, 2);

  const dateOptions: IListItem[] = [
    {
      value: originalDate,
      label: `${format(original, "dd/MM/yyyy", { locale: es })} (Original)`,
    },
    {
      value: plus1Month.toISOString(),
      label: `${format(plus1Month, "dd/MM/yyyy", { locale: es })} (+1 mes)`,
    },
    {
      value: plus2Months.toISOString(),
      label: `${format(plus2Months, "dd/MM/yyyy", { locale: es })} (+2 meses)`,
    },
  ];

  const onItemClick = (item: IListItem) => {
    setOptionsExpanded(false);
    if (onSelect) onSelect(item.value as string);
  };

  const handleClickAway = () => {
    setOptionsExpanded(false);
    // Also trigger the current value to close the selector
    if (onSelect) onSelect(selectedDate);
  };

  const toggleOptionsExpanded = () => {
    setOptionsExpanded(!optionsExpanded);
  };

  const classes = useStyles();

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <SelectorRoot>
        {optionsExpanded && (
          <ListPaper>
            <List>
              {dateOptions.map((item, idx) => (
                <ListItem
                  button
                  divider={idx !== dateOptions.length - 1}
                  onClick={() => {
                    onItemClick(item);
                  }}
                  key={idx}
                >
                  <ListItemText primary={item.label} disableTypography />
                </ListItem>
              ))}
            </List>
          </ListPaper>
        )}
        <IconButton
          onClick={toggleOptionsExpanded}
          className={
            optionsExpanded
              ? classes.selectorExpanded
              : classes.selectorNotExpanded
          }
          size="large">
          <InputBox>
            {dateOptions.find((x) => x.value === selectedDate)?.label}
          </InputBox>
          <IconExpand>
            <ExpandMoreIcon />
          </IconExpand>
        </IconButton>
      </SelectorRoot>
    </ClickAwayListener>
  );
};

export default PrimerVencimientoSelector;

const SelectorRoot = styled("div")({
  display: "inline-block",
  background: "white",
  position: "relative",
  height: pxToRem(30),
  minWidth: pxToRem(250),
});

const InputBox = styled("div")({
  display: "flex",
  padding: "0px 20px 0px 0px",
  flex: 1,
  fontSize: 12,
  fontWeight: "bold",
  lineHeight: "1.25",
  textAlign: "left",
  color: "grey",
});

const IconExpand = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderLeft: "solid 1px #e4e4e6",
  borderRadius: "0px 5px 5px 0px",
  padding: "0px 10px 0px 10px",
  color: "grey",
  height: "100%",
});

const ListPaper = styled("div")({
  border: "solid 1px #e4e4e6",
  borderRadius: "5px 5px 0 0",
  display: "block",
  position: "absolute",
  zIndex: 2,
  width: "100%",
  background: "white",
  bottom: pxToRem(30),
});

const List = styled(MuiList)({
  padding: 0,
});

const ListItemText = styled(MuiListItemText)({
  display: "flex",
  flex: 1,
  fontSize: 12,
  lineHeight: "1.25",
  textAlign: "left",
  color: "grey",
  paddingLeft: 0,
  margin: 0,
});

const ExpandMoreIcon = styled(MuiExpandMoreIcon)({
  width: "1.8rem",
  height: "100%",
});

const useStyles = makeStyles({
  selectorExpanded: {
    display: "flex",
    padding: "0px 0px 0px 10px",
    borderRadius: "0 0 5px 5px",
    borderLeft: "solid 1px #e4e4e6",
    borderRight: "solid 1px #e4e4e6",
    borderBottom: "solid 1px #e4e4e6",
    backgroundColor: "white",
    justifyContent: "center",
    alignItems: "center",
    height: pxToRem(30),
    width: "100%",
  },
  selectorNotExpanded: {
    display: "flex",
    padding: "0px 0px 0px 10px",
    borderRadius: "5px",
    border: "solid 1px #e4e4e6",
    backgroundColor: "white",
    justifyContent: "center",
    alignItems: "center",
    height: pxToRem(30),
    width: "100%",
  },
});
